import { useEffect, useState } from "react";

/**
 * localStorage key holding the user's persisted display settings
 * (view mode, compact mode, grid density).
 */
export const TRACK_VIEW_STORAGE_KEY = "TrackManagement:viewSettings";

const VIEW_MODES = ["grid", "list"];
const GRID_SIZES = [4, 6, 8];

/**
 * Read persisted display settings, ignoring anything malformed or stale so a
 * bad/legacy payload can never break the view.
 *
 * @returns {Object} Partial settings object (empty when nothing usable stored)
 */
const readStoredView = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(TRACK_VIEW_STORAGE_KEY);
    const stored = raw ? JSON.parse(raw) : null;
    if (!stored || typeof stored !== "object") return {};

    const settings = {};
    if (VIEW_MODES.includes(stored.viewMode)) {
      settings.viewMode = stored.viewMode;
    }
    if (typeof stored.compactMode === "boolean") {
      settings.compactMode = stored.compactMode;
    }
    if (GRID_SIZES.includes(stored.gridSize)) {
      settings.gridSize = stored.gridSize;
    }
    return settings;
  } catch (err) {
    return {};
  }
};

/**
 * Hook for managing view mode, compact mode, and grid size
 *
 * Settings are memorized in localStorage and preloaded on the next visit;
 * the `initial*` params are only used the first time (or when storage is
 * unavailable/invalid).
 *
 * @param {Object} params
 * @param {string} params.initialViewMode - Initial view mode ("grid" or "list")
 * @param {boolean} params.initialCompactMode - Initial compact mode state
 * @param {number} params.initialGridSize - Initial grid size (4, 6, or 8)
 * @returns {Object} View state and setters
 */
export const useTrackView = ({
  initialViewMode = "grid",
  initialCompactMode = false,
  initialGridSize = 4,
}) => {
  // Read once on mount so subsequent renders never touch localStorage.
  const [storedView] = useState(readStoredView);

  const [viewMode, setViewMode] = useState(
    storedView.viewMode ?? initialViewMode,
  );
  const [compactMode, setCompactMode] = useState(
    storedView.compactMode ?? initialCompactMode,
  );
  const [gridSize, setGridSize] = useState(
    storedView.gridSize ?? initialGridSize,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        TRACK_VIEW_STORAGE_KEY,
        JSON.stringify({ viewMode, compactMode, gridSize }),
      );
    } catch (err) {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, [viewMode, compactMode, gridSize]);

  return {
    viewMode,
    setViewMode,
    compactMode,
    setCompactMode,
    gridSize,
    setGridSize,
  };
};
